import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { customersApi, type Customer } from '@/api/customers';
import { getGenders } from '@/api/genders';
import { getApiErrorMessage } from '@/api/errors';

interface CreateCustomerModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  onCreated: (customer: Customer) => void;
  /** Prefills the name field from whatever the staff member already typed into the search box. */
  initialFirstName?: string;
}

/**
 * Mirrors the backend's CustomerRequestDto rules so staff get the error inline instead of
 * a 400: first name required, and at least one contact channel so the customer is reachable.
 * Last name is optional on purpose — walk-ins are often logged by first name only.
 */
function getValidationError(values: {
  firstName: string;
  email: string;
  phone: string;
}): string | null {
  if (!values.firstName.trim()) return 'El nombre es obligatorio.';
  if (!values.email.trim() && !values.phone.trim())
    return 'Agrega al menos un email o un teléfono.';
  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()))
    return 'El email no es válido.';
  if (values.phone.trim() && values.phone.replace(/\D/g, '').length < 7)
    return 'El teléfono no es válido.';
  return null;
}

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--muted-fg)',
  marginBottom: '6px',
} as const;

const fieldStyle = {
  width: '100%',
  height: '44px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid var(--border-color)',
  backgroundColor: 'var(--off-white)',
  fontSize: '14px',
  color: 'var(--dark-teal)',
} as const;

export function CreateCustomerModal({
  open,
  onClose,
  businessId,
  onCreated,
  initialFirstName = '',
}: CreateCustomerModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [genderId, setGenderId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: genders = [] } = useQuery({
    queryKey: ['genders'],
    queryFn: getGenders,
    staleTime: 60 * 60_000,
    enabled: open,
  });

  // Reset on every open so a cancelled entry never leaks into the next one. The
  // search term seeds the name field, which is what staff just typed anyway.
  useEffect(() => {
    if (!open) return;
    setFirstName(initialFirstName.trim());
    setLastName('');
    setPhone('');
    setEmail('');
    setGenderId('');
    setNotes('');
    setError(null);
    setSubmitting(false);
  }, [open, initialFirstName]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = getValidationError({ firstName, email, phone });
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const created = await customersApi.create(businessId, {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        genderId: genderId ? Number(genderId) : undefined,
        notes: notes.trim() || undefined,
      });
      onCreated(created);
    } catch (err) {
      setError(getApiErrorMessage(err, 'No se pudo crear el cliente. Intenta de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="rounded-2xl w-full max-w-md flex flex-col"
        style={{
          backgroundColor: 'var(--off-white)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-lg)',
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-customer-title"
      >
        <div className="flex items-start justify-between gap-3" style={{ padding: '20px 20px 0' }}>
          <div>
            <h2
              id="create-customer-title"
              style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}
            >
              Nuevo cliente
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--muted-fg)', marginTop: '4px', lineHeight: 1.4 }}>
              Agrega al menos un email o un teléfono para poder contactarlo.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ width: '32px', height: '32px', color: 'var(--muted-fg)' }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="overflow-auto" style={{ padding: '16px 20px' }}>
            {error && (
              <div
                role="alert"
                className="rounded-xl mb-4"
                style={{
                  padding: '10px 12px',
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                  color: 'var(--destructive)',
                  fontSize: '13px',
                }}
              >
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label style={labelStyle} htmlFor="new-customer-first-name">
                  Nombre <span style={{ color: 'var(--destructive)' }}>*</span>
                </label>
                <input
                  id="new-customer-first-name"
                  type="text"
                  autoFocus
                  maxLength={100}
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  disabled={submitting}
                  placeholder="Ej. María"
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="new-customer-last-name">Apellido</label>
                <input
                  id="new-customer-last-name"
                  type="text"
                  maxLength={100}
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  disabled={submitting}
                  placeholder="Ej. Pérez"
                  style={fieldStyle}
                />
              </div>
            </div>

            <div className="mb-4">
              <label style={labelStyle} htmlFor="new-customer-phone">Teléfono</label>
              <input
                id="new-customer-phone"
                type="tel"
                inputMode="tel"
                maxLength={20}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                disabled={submitting}
                placeholder="809-555-1234"
                style={fieldStyle}
              />
            </div>

            <div className="mb-4">
              <label style={labelStyle} htmlFor="new-customer-email">Email</label>
              <input
                id="new-customer-email"
                type="email"
                inputMode="email"
                maxLength={200}
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={submitting}
                placeholder="maria@email.com"
                style={fieldStyle}
              />
            </div>

            {genders.length > 0 && (
              <div className="mb-4">
                <label style={labelStyle} htmlFor="new-customer-gender">Género</label>
                <select
                  id="new-customer-gender"
                  value={genderId}
                  onChange={e => setGenderId(e.target.value)}
                  disabled={submitting}
                  style={fieldStyle}
                >
                  <option value="">Prefiero no especificarlo</option>
                  {genders.map(g => (
                    <option key={g.id} value={String(g.id)}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={labelStyle} htmlFor="new-customer-notes">Notas (opcional)</label>
              <textarea
                id="new-customer-notes"
                rows={3}
                maxLength={1000}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                disabled={submitting}
                placeholder="Alergias, preferencias, referido por..."
                style={{ ...fieldStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }}
              />
            </div>
          </div>

          <div
            className="flex gap-3 flex-shrink-0"
            style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)' }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="rounded-xl"
              style={{
                height: '44px',
                padding: '0 20px',
                border: '1px solid var(--border-color)',
                color: 'var(--dark-teal)',
                fontSize: '14px',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-xl flex items-center justify-center gap-2"
              style={{
                height: '44px',
                backgroundColor: 'var(--terracotta)',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                border: 'none',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Guardando...' : 'Agregar cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
